import React, { useState, useEffect, useRef } from "react";
import { toast, Bounce, ToastContainer } from "react-toastify";
import ToggleButtons from "./reusable/toggleButtons";
import InputBox from "./reusable/InputBox";
import { calculatePriceBasedOnFat, calculatePriceBasedOnFatAndSNF } from "../utils/calculatePrice";
import milkEntryService from "../services/milkEntryService";
import html2canvas from "html2canvas";
import { FaPrint, FaShareAlt, FaTimes, FaCheckCircle } from "react-icons/fa";

function MilkEntries({ userId, smallScreen, role, Name }) {
  const [inputs, setInputs] = useState({
    date: "",
    weight: "",
    fat: "",
    snf: "",
    price: "",
  });
  const [time, setTime] = useState("");
  const [animalType, setAnimalType] = useState("cow");
  const [calculationType, setCalculationType] = useState("fat");
  const [isAutoIncrement, setIsAutoIncrement] = useState(() => localStorage.getItem("autoIncrement") === "true");
  const [showReceiptPopup, setShowReceiptPopup] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const receiptRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [showReceiptAfterSubmit, setShowReceiptAfterSubmit] = useState(() => {
    const saved = localStorage.getItem("showReceiptAfterSubmit");
    return saved === null ? true : saved === "true";
  });

  const weightRef = useRef();
  const fatRef = useRef();
  const snfRef = useRef();
  const priceRef = useRef();

  useEffect(() => {
    const todayDate = new Date().toISOString().split("T")[0];
    setInputs((prev) => ({ ...prev, date: todayDate }));
  }, []);

  useEffect(() => {
    localStorage.setItem("showReceiptAfterSubmit", showReceiptAfterSubmit);
  }, [showReceiptAfterSubmit]);

  useEffect(() => {
    let calculatedPrice = "";
    if (calculationType === "fat") {
      calculatedPrice = calculatePriceBasedOnFat(animalType, inputs.fat);
    } else if (calculationType === "Fat And Snf") {
      calculatedPrice = calculatePriceBasedOnFatAndSNF(animalType, inputs.fat, inputs.snf);
    }
    setInputs((prev) => ({ ...prev, price: calculatedPrice }));
  }, [inputs.fat, inputs.snf, calculationType, animalType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  const finalTotal = inputs.weight && inputs.price
    ? (parseFloat(inputs.weight) * parseFloat(inputs.price)).toFixed(2)
    : "0.00";

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (userId === "" && role === "admin") {
      setIsOpen(true);
      return;
    }

    const receiptPayload = {
      ...inputs,
      time,
      animalType,
      total: finalTotal,
      calculationType,
      userName: Name
    };

    try {
      const { updatedInputs, newDate } = await toast.promise(
        milkEntryService({ ...inputs, animalType }, time, isAutoIncrement, userId),
        {
          pending: "Submitting entry...",
          success: "Entry recorded! 👌",
          error: "Submission failed 🤯",
        }
      );

      setInputs((prev) => ({
        ...prev,
        ...updatedInputs,
        date: isAutoIncrement && newDate ? newDate : prev.date
      }));

      if (role === "admin" && showReceiptAfterSubmit) {
        setReceiptData(receiptPayload);
        setShowReceiptPopup(true);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const isAndroidWebView = (window.AndroidBridge && typeof window.AndroidBridge.printImage === 'function') || (/Android/i.test(navigator.userAgent));

  // ===================== PRINT RECEIPT =====================
  const printReceipt = async () => {
    if (!receiptRef.current || !receiptData) {
      toast.error("Receipt not ready");
      return;
    }

    if (isAndroidWebView) {
      try {
        const canvas = await html2canvas(receiptRef.current, { scale: 2, backgroundColor: "#ffffff" });
        const base64Image = canvas.toDataURL("image/png");

        if (window.AndroidBridge?.printImage) {
          window.AndroidBridge.printImage(base64Image);
        } else {
          toast.error("Android printing not supported");
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to print receipt");
      }
    } else {
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        toast.error("Popup blocked");
        return;
      }

      printWindow.document.write(`
        <html>
          <head>
            <title>Milk Receipt</title>
            <style>
              body { font-family: 'Courier New', monospace; width: 300px; margin: 0 auto; padding: 15px; }
              .row { display: flex; justify-content: space-between; margin-bottom: 4px; }
              .divider { border-top: 1px dashed #000; margin: 8px 0; }
              .center { text-align: center; font-weight: bold; }
            </style>
          </head>
          <body>
            <div class="center">MILK RECEIPT</div>
            <div class="divider"></div>

            <div class="row"><span>Date:</span><span>${receiptData.date}</span></div>
            <div class="row"><span>Time:</span><span>${receiptData.time}</span></div>
            <div class="row"><span>User:</span><span>${Name}</span></div>

            <div class="divider"></div>

            <div class="row"><span>Animal:</span><span>${receiptData.animalType}</span></div>
            <div class="row"><span>Calc:</span><span>${receiptData.calculationType}</span></div>
            <div class="row"><span>Weight:</span><span>${receiptData.weight} kg</span></div>
            <div class="row"><span>Fat:</span><span>${receiptData.fat}%</span></div>
            ${receiptData.snf ? `<div class="row"><span>SNF:</span><span>${receiptData.snf}%</span></div>` : ""}
            <div class="row"><span>Rate:</span><span>₹${receiptData.price}</span></div>

            <div class="divider"></div>

            <div class="row" style="font-weight:bold">
              <span>TOTAL:</span>
              <span>₹${receiptData.total}</span>
            </div>

            <p style="text-align:center;font-size:11px;margin-top:10px">
              Generated on ${new Date().toLocaleString()}
            </p>
          </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  };

  // ===================== SHARE RECEIPT =====================
  const shareReceipt = async () => {
    if (!receiptRef.current || !receiptData) return;

    try {
      const canvas = await html2canvas(receiptRef.current, { scale: 2, backgroundColor: "#ffffff" });
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      const file = new File([blob], 'milk-receipt.png', { type: 'image/png' });

      if (navigator.share && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'Milk Receipt', text: `Milk transaction receipt for ${Name}` });
      } else {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'milk-receipt.png';
        link.click();
        toast.info("Receipt downloaded. You can share it from your device.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to share receipt");
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 ">
      {/* ===================== HIDDEN RECEIPT ===================== */}
      {receiptData && (
        <div ref={receiptRef} className="absolute left-[-9999px] top-0 w-[300px] bg-white p-4" style={{ fontFamily: "'Courier New', monospace", color: "#000" }}>
          <div className="text-center font-bold text-lg mb-2 pb-1 border-b border-dashed border-gray-800">MILK RECEIPT</div>
          <div className="mb-3">
            <div className="flex justify-between mb-1"><span>Date:</span><span>{receiptData.date}</span></div>
            <div className="flex justify-between mb-1"><span>Time:</span><span>{receiptData.time}</span></div>
            <div className="flex justify-between mb-1"><span>User:</span><span>{Name}</span></div>
          </div>
          <div className="border-t border-dashed border-gray-800 my-2" />
          <div className="mb-3">
            <div className="flex justify-between mb-1"><span>Animal Type:</span><span>{receiptData.animalType}</span></div>
            <div className="flex justify-between mb-1"><span>Calculation:</span><span>{receiptData.calculationType}</span></div>
            <div className="flex justify-between mb-1"><span>Weight:</span><span>{receiptData.weight} kg</span></div>
            <div className="flex justify-between mb-1"><span>Fat:</span><span>{receiptData.fat}%</span></div>
            {receiptData.snf && <div className="flex justify-between mb-1"><span>SNF:</span><span>{receiptData.snf}%</span></div>}
            <div className="flex justify-between mb-1"><span>Rate:</span><span>₹{receiptData.price}</span></div>
          </div>
          <div className="border-t border-dashed border-gray-800 my-2" />
          <div className="flex justify-between font-bold text-base"><span>TOTAL:</span><span>₹{receiptData.total}</span></div>
          <div className="text-center text-xs mt-4 text-gray-600">Generated on {new Date().toLocaleString()}</div>
        </div>
      )}

      {/* ===================== MODAL / FORM / UI ===================== */}
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="bg-gray-50/50 p-4 md:p-6 space-y-4 border-b border-gray-100">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-2 ml-1">Calculation</p>
              <ToggleButtons name1="fat" name2="Fat And Snf" onToggle={setCalculationType} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-2 ml-1">Animal</p>
                <ToggleButtons name1="cow" name2="buffalo" onToggle={setAnimalType} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-2 ml-1">Shift</p>
                <ToggleButtons name1="morning" name2="evening" onToggle={setTime} />
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-4 md:p-8 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <InputBox label="Date" type="date" name="date" value={inputs.date} onValueChange={handleChange} required />
            <InputBox reference={weightRef} label="Weight (kg)" type="number" name="weight" value={inputs.weight} onValueChange={handleChange} placeholder="0.00" required onNext={() => fatRef.current.focus()} />
            <InputBox reference={fatRef} label="Fat (%)" type="number" name="fat" value={inputs.fat} onValueChange={handleChange} placeholder="0.0" required onNext={() => calculationType === 'fat' ? priceRef.current.focus() : snfRef.current.focus()} />
            {calculationType !== 'fat' && <InputBox reference={snfRef} label="SNF (%)" type="number" name="snf" value={inputs.snf} onValueChange={handleChange} placeholder="0.0" required onNext={() => priceRef.current.focus()} />}
            <InputBox reference={priceRef} label="Rate (₹/kg)" type="number" name="price" value={inputs.price} onValueChange={handleChange} required />
            <div className="flex flex-col justify-end">
              <label className="text-xs font-bold text-gray-500 uppercase mb-1 ml-1">Total Amount</label>
              <div className="h-[46px] flex items-center px-4 rounded-xl bg-indigo-50 border border-indigo-100 text-indigo-700 font-black text-lg">
                ₹ {finalTotal}
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 py-2 border-t border-gray-50 pt-6">
            <label className="flex items-center space-x-3 cursor-pointer group">
              <input type="checkbox" checked={isAutoIncrement} onChange={() => setIsAutoIncrement(!isAutoIncrement)} className="w-5 h-5 rounded-md border-gray-300 text-indigo-600 focus:ring-indigo-500" />
              <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors">Auto-next date</span>
            </label>
            <label className="flex items-center space-x-3 cursor-pointer group">
              <input type="checkbox" checked={showReceiptAfterSubmit} onChange={() => setShowReceiptAfterSubmit(!showReceiptAfterSubmit)} className="w-5 h-5 rounded-md border-gray-300 text-indigo-600 focus:ring-indigo-500" />
              <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors">Generate Receipt</span>
            </label>
          </div>

          <button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-4 rounded-2xl shadow-lg shadow-indigo-200 transition-all active:scale-[0.98]">Save Entry</button>
        </form>
      </div>

      {/* ===================== RECEIPT POPUP ===================== */}
      {showReceiptPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">
          <div className="bg-white w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaCheckCircle size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Success!</h3>
              <p className="text-gray-500 text-sm mt-1">Transaction recorded for {Name}</p>
            </div>

            <div className="px-6 pb-8 space-y-3">
              <button onClick={printReceipt} className="w-full flex items-center justify-center gap-3 py-3 bg-gray-900 text-white rounded-xl font-semibold hover:bg-black transition-colors">
                <FaPrint /> Print Receipt
              </button>
              <button onClick={shareReceipt} className="w-full flex items-center justify-center gap-3 py-3 bg-indigo-50 text-indigo-700 rounded-xl font-semibold hover:bg-indigo-100 transition-colors">
                <FaShareAlt /> Share on WhatsApp
              </button>
              <button onClick={() => setShowReceiptPopup(false)} className="w-full py-3 text-gray-400 text-sm font-medium hover:text-gray-600">Dismiss</button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== CUSTOMER WARNING ===================== */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-red-900/20 backdrop-blur-md">
          <div className="bg-white rounded-2xl p-6 max-w-xs text-center shadow-xl border border-red-100">
            <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaTimes />
            </div>
            <h3 className="font-bold text-gray-900">No Customer Selected</h3>
            <p className="text-sm text-gray-500 mt-2">Please select a customer from the list before saving this entry.</p>
            <button onClick={() => setIsOpen(false)} className="mt-6 w-full py-2 bg-gray-900 text-white rounded-lg">Okay</button>
          </div>
        </div>
      )}

      <ToastContainer position="top-center" transition={Bounce} />
    </div>
  );
}

export default MilkEntries;
